// SCR-STAFF-205 — قراردادهای سازمانی و ذی‌نفعان (US-CAT-040، 041): تعریف قرارداد با فهرست قیمت اختصاصی یا درصد تخفیف،
// سقف هر ذی‌نفع و کل، پرداخت‌کننده و سهم سازمان (AC-CAT-040.1، 040.2)؛ فعال‌سازی و پایان؛ ثبت ذی‌نفع با نسبت و بازه اعتبار
// (AC-CAT-041.1) با cat.contract.enroll. قراردادهای هم‌پوشان جمع نمی‌شوند و بهترین شرایط اعمال می‌شود (BR-CAT-040).
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type BeneficiaryView, type ContractView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  ErrorState,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  applyServerErrors,
  faDigits,
  zBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { catKeys, usePriceLists } from './common';

export const SCR_STAFF_205 = 'SCR-STAFF-205';

const PAYERS = ['Beneficiary', 'Organization'] as const;
const RELATIONS = ['Self', 'Spouse', 'Child', 'Other'] as const;

const contractSchema = z
  .object({
    code: z.string().trim().min(1, 'common:validation.required').max(40, 'common:validation.max_length'),
    organizationName: z.string().trim().min(1, 'common:validation.required').max(200, 'common:validation.max_length'),
    validFrom: zBusinessDate(),
    validTo: zBusinessDate().optional().or(z.literal('')),
    terms: z.enum(['Discount', 'PriceList']),
    discountPercent: z.string(),
    priceListId: z.string(),
    payer: z.enum(PAYERS),
    organizationSharePercent: z.string(),
    perBeneficiaryCap: z.number().int().optional(),
    totalCap: z.number().int().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.terms === 'Discount' && !(Number(value.discountPercent) >= 1 && Number(value.discountPercent) <= 100)) {
      ctx.addIssue({ code: 'custom', path: ['discountPercent'], message: 'errors:validation.invalid' });
    }

    if (value.terms === 'PriceList' && !value.priceListId) {
      ctx.addIssue({ code: 'custom', path: ['priceListId'], message: 'common:validation.required' });
    }

    if (
      value.payer === 'Organization' &&
      !(Number(value.organizationSharePercent) >= 1 && Number(value.organizationSharePercent) <= 100)
    ) {
      ctx.addIssue({ code: 'custom', path: ['organizationSharePercent'], message: 'errors:validation.invalid' });
    }

    if (value.validTo && value.validTo < value.validFrom) {
      ctx.addIssue({ code: 'custom', path: ['validTo'], message: 'errors:validation.range' });
    }
  });
type ContractForm = z.input<typeof contractSchema>;

const beneficiarySchema = z.object({
  patientId: z.string().trim().uuid('errors:validation.invalid'),
  membershipNumber: z.string().trim().max(40, 'common:validation.max_length').optional().or(z.literal('')),
  relation: z.enum(RELATIONS),
  validFrom: zBusinessDate().optional().or(z.literal('')),
  validTo: zBusinessDate().optional().or(z.literal('')),
});
type BeneficiaryForm = z.input<typeof beneficiarySchema>;

export function ContractsPage() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.contract.manage');
  const enroll = useWriteAccess('cat.contract.enroll');
  const idempotency = useIdempotencyKey();
  const lists = usePriceLists();
  const [selected, setSelected] = useState<ContractView | null>(null);
  const [activating, setActivating] = useState<ContractView | null>(null);
  const [expiring, setExpiring] = useState<ContractView | null>(null);
  const [suspending, setSuspending] = useState<ContractView | null>(null);
  const [submitError, setSubmitError] = useState<unknown>();

  const contracts = useQuery({
    queryKey: catKeys.contracts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/organization-contracts')),
  });

  const beneficiaries = useQuery({
    enabled: selected !== null,
    queryKey: catKeys.beneficiaries(session?.tenantId, selected?.id ?? ''),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/organization-contracts/{id}/beneficiaries', {
          params: { path: { id: selected?.id ?? '' } },
        }),
      ),
  });

  const contractForm = useForm<ContractForm>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      code: '',
      organizationName: '',
      validFrom: '',
      validTo: '',
      terms: 'Discount',
      discountPercent: '',
      priceListId: '',
      payer: 'Beneficiary',
      organizationSharePercent: '100',
      perBeneficiaryCap: undefined,
      totalCap: undefined,
    },
  });
  const terms = useWatch({ control: contractForm.control, name: 'terms' });
  const payer = useWatch({ control: contractForm.control, name: 'payer' });

  const beneficiaryForm = useForm<BeneficiaryForm>({
    resolver: zodResolver(beneficiarySchema),
    defaultValues: { patientId: '', membershipNumber: '', relation: 'Self', validFrom: '', validTo: '' },
  });

  const create = useMutation({
    mutationFn: (form: ContractForm) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/organization-contracts', {
          body: {
            code: form.code,
            organizationName: form.organizationName,
            validFrom: form.validFrom,
            validTo: form.validTo || null,
            priceListId: form.terms === 'PriceList' ? form.priceListId : null,
            discountPercent: form.terms === 'Discount' ? Number(form.discountPercent) : null,
            payer: form.payer,
            organizationSharePercent:
              form.payer === 'Organization' ? Number(form.organizationSharePercent) : null,
            perBeneficiaryCap:
              form.perBeneficiaryCap === undefined ? null : { amount: form.perBeneficiaryCap, currency: 'IRR' },
            totalCap: form.totalCap === undefined ? null : { amount: form.totalCap, currency: 'IRR' },
            categoryIds: [],
            serviceIds: [],
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      contractForm.reset();
      void queryClient.invalidateQueries({ queryKey: catKeys.contracts(session?.tenantId) });
    },
    onError: (error) => {
      const count = applyServerErrors(error, contractForm.setError, {
        fields: ['code', 'discountPercent', 'priceListId', 'organizationSharePercent', 'validTo'],
        codeFields: { 'PHX-CAT-0341': 'code' },
      });
      setSubmitError(count > 0 ? undefined : error);
    },
  });

  const addBeneficiary = useMutation({
    mutationFn: (form: BeneficiaryForm) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/organization-contracts/{id}/beneficiaries', {
          params: { path: { id: selected?.id ?? '' } },
          body: {
            patientId: form.patientId,
            membershipNumber: form.membershipNumber || null,
            relation: form.relation,
            validFrom: form.validFrom || null,
            validTo: form.validTo || null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      beneficiaryForm.reset();
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      applyServerErrors(error, beneficiaryForm.setError, {
        fields: ['patientId', 'validTo'],
        codeFields: { 'PHX-CAT-0344': 'patientId', 'PHX-CAT-0342': 'patientId' },
      });
    },
  });

  const transition = useMutation({
    mutationFn: (input: { id: string; action: 'activate' | 'suspend' | 'expire' }) => {
      const options = {
        params: { path: { id: input.id } },
        headers: { 'Idempotency-Key': idempotency.current() },
      } as const;

      if (input.action === 'activate') {
        return unwrap(tenantApi.POST('/api/v1/catalog/organization-contracts/{id}:activate', options));
      }

      if (input.action === 'suspend') {
        return unwrap(tenantApi.POST('/api/v1/catalog/organization-contracts/{id}:suspend', options));
      }

      return unwrap(tenantApi.POST('/api/v1/catalog/organization-contracts/{id}:expire', options));
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setActivating(null);
      setExpiring(null);
      setSuspending(null);
      void queryClient.invalidateQueries({ queryKey: catKeys.contracts(session?.tenantId) });
    },
  });

  const contractColumns: DataTableColumn<ContractView>[] = [
    { id: 'code', headerKey: 'cat:contracts.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'organization', headerKey: 'cat:contracts.columns.organization', cell: (row) => row.organizationName },
    {
      id: 'validity',
      headerKey: 'cat:contracts.columns.validity',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <JalaliDate value={row.validFrom} />
          <span>—</span>
          {row.validTo ? <JalaliDate value={row.validTo} /> : <span>{t('cat:contracts.open_ended')}</span>}
        </Stack>
      ),
    },
    {
      id: 'terms',
      headerKey: 'cat:contracts.columns.terms',
      cell: (row) =>
        row.discountPercent === null || row.discountPercent === undefined
          ? (row.priceListName ?? t('cat:contracts.own_price_list'))
          : t('cat:contracts.discount_value', { value: faDigits(row.discountPercent) }),
    },
    {
      id: 'payer',
      headerKey: 'cat:contracts.columns.payer',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{t(`cat:contracts.payer.${row.payer}`)}</span>
          {row.payer === 'Organization' && (
            <Typography variant="caption" color="text.secondary">
              {t('cat:contracts.share_value', { value: faDigits(row.organizationSharePercent) })}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'caps',
      headerKey: 'cat:contracts.columns.caps',
      cell: (row) => (
        <Stack spacing={0.25}>
          {row.perBeneficiaryCap && (
            <span>
              {t('cat:contracts.per_beneficiary')}{' '}
              <Money amountMinor={row.perBeneficiaryCap.amount} currency={row.perBeneficiaryCap.currency} />
            </span>
          )}
          {row.totalCap && (
            <span>
              {t('cat:contracts.total_cap')}{' '}
              <Money amountMinor={row.totalCap.amount} currency={row.totalCap.currency} />
            </span>
          )}
          {!row.perBeneficiaryCap && !row.totalCap && <span>{t('cat:contracts.no_cap')}</span>}
        </Stack>
      ),
    },
    {
      id: 'beneficiaries',
      headerKey: 'cat:contracts.columns.beneficiaries',
      cell: (row) => faDigits(row.beneficiaryCount),
    },
    {
      id: 'status',
      headerKey: 'cat:contracts.columns.status',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Active' ? 'active' : row.status === 'Expired' ? 'cancelled' : 'pending'}
          labelKey={`cat:contracts.status.${row.status}`}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'cat:prices.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setSelected(row)} data-testid={`contract-select-${row.code}`}>
            {t('cat:contracts.beneficiaries')}
          </Button>
          {(row.status === 'Draft' || row.status === 'Suspended') && (
            <Button
              size="small"
              variant="outlined"
              disabled={!write.canWrite}
              onClick={() => setActivating(row)}
              data-testid={`contract-activate-${row.code}`}
            >
              {t(row.status === 'Suspended' ? 'cat:contracts.resume' : 'cat:contracts.activate')}
            </Button>
          )}
          {row.status === 'Active' && (
            <Button
              size="small"
              disabled={!write.canWrite}
              onClick={() => setSuspending(row)}
              data-testid={`contract-suspend-${row.code}`}
            >
              {t('cat:contracts.suspend')}
            </Button>
          )}
          {row.status !== 'Expired' && (
            <Button
              size="small"
              color="error"
              disabled={!write.canWrite}
              onClick={() => setExpiring(row)}
              data-testid={`contract-expire-${row.code}`}
            >
              {t('cat:contracts.expire')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  const beneficiaryColumns: DataTableColumn<BeneficiaryView>[] = [
    { id: 'patient', headerKey: 'cat:contracts.columns.patient', cell: (row) => <bdi dir="ltr">{row.patientId}</bdi> },
    {
      id: 'membershipNumber',
      headerKey: 'cat:contracts.columns.membership_number',
      cell: (row) => (row.membershipNumber ? <bdi dir="ltr">{row.membershipNumber}</bdi> : '—'),
    },
    {
      id: 'relation',
      headerKey: 'cat:contracts.columns.relation',
      cell: (row) => t(`cat:contracts.relation.${row.relation}`),
    },
    {
      id: 'validity',
      headerKey: 'cat:contracts.columns.validity',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <JalaliDate value={row.validFrom} />
          <span>—</span>
          {row.validTo ? <JalaliDate value={row.validTo} /> : <span>{t('cat:contracts.open_ended')}</span>}
        </Stack>
      ),
    },
    {
      id: 'used',
      headerKey: 'cat:contracts.columns.used',
      cell: (row) => <Money amountMinor={row.used.amount} currency={row.used.currency} />,
    },
    {
      id: 'effective',
      headerKey: 'cat:contracts.columns.effective',
      cell: (row) => (
        <StatusChip
          tone={row.isEffective ? 'active' : 'cancelled'}
          labelKey={row.isEffective ? 'cat:contracts.effective' : 'cat:contracts.not_effective'}
        />
      ),
    },
  ];

  const contractLists = (lists.data?.items ?? []).filter((list) => list.type === 'Contract');

  return (
    <ListPage
      screenId={SCR_STAFF_205}
      titleKey="cat:contracts.title"
      subtitleKey="cat:contracts.subtitle"
      filters={<WriteNotice write={write} />}
    >
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void contractForm.handleSubmit((form) => create.mutate(form))();
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1">{t('cat:contracts.add_title')}</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextFieldRHF control={contractForm.control} name="code" labelKey="cat:contracts.code" required />
                <TextFieldRHF
                  control={contractForm.control}
                  name="organizationName"
                  labelKey="cat:contracts.organization"
                  required
                />
                <JalaliDatePickerRHF
                  control={contractForm.control}
                  name="validFrom"
                  labelKey="cat:contracts.valid_from"
                  required
                />
                <JalaliDatePickerRHF control={contractForm.control} name="validTo" labelKey="cat:contracts.valid_to" />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <SelectRHF
                  control={contractForm.control}
                  name="terms"
                  labelKey="cat:contracts.terms"
                  options={[
                    { value: 'Discount', labelKey: 'cat:contracts.terms_discount' },
                    { value: 'PriceList', labelKey: 'cat:contracts.terms_price_list' },
                  ]}
                />
                {terms === 'Discount' ? (
                  <TextFieldRHF
                    control={contractForm.control}
                    name="discountPercent"
                    labelKey="cat:contracts.discount_percent"
                    required
                  />
                ) : (
                  <SelectRHF
                    control={contractForm.control}
                    name="priceListId"
                    labelKey="cat:contracts.price_list"
                    options={contractLists.map((list) => ({ value: list.id, label: list.name }))}
                    required
                  />
                )}
                <SelectRHF
                  control={contractForm.control}
                  name="payer"
                  labelKey="cat:contracts.columns.payer"
                  options={PAYERS.map((value) => ({ value, labelKey: `cat:contracts.payer.${value}` }))}
                />
                {payer === 'Organization' && (
                  <TextFieldRHF
                    control={contractForm.control}
                    name="organizationSharePercent"
                    labelKey="cat:contracts.share_percent"
                    required
                  />
                )}
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <MoneyFieldRHF
                  control={contractForm.control}
                  name="perBeneficiaryCap"
                  labelKey="cat:contracts.per_beneficiary_cap"
                />
                <MoneyFieldRHF control={contractForm.control} name="totalCap" labelKey="cat:contracts.total_cap_field" />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!write.canWrite || create.isPending}
                  data-testid="contract-create"
                >
                  {t('cat:contracts.add')}
                </Button>
              </Stack>
              {submitError !== undefined && <ErrorState error={submitError} />}
            </Stack>
          </Box>
        </Paper>

        <DataTable
          captionKey="cat:contracts.caption"
          columns={contractColumns}
          rows={contracts.data?.items}
          getRowId={(row) => row.id}
          isLoading={contracts.isPending}
          error={contracts.error}
          onRetry={() => void contracts.refetch()}
          emptyTitleKey="cat:contracts.empty"
        />

        {selected !== null && (
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="contract-beneficiaries-panel">
            <Stack spacing={1.5}>
              <Typography variant="subtitle1">
                {t('cat:contracts.beneficiaries_of', { name: selected.organizationName })}
              </Typography>
              <Box
                component="form"
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  void beneficiaryForm.handleSubmit((form) => addBeneficiary.mutate(form))();
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextFieldRHF
                    control={beneficiaryForm.control}
                    name="patientId"
                    labelKey="cat:contracts.patient_id"
                    required
                  />
                  <TextFieldRHF
                    control={beneficiaryForm.control}
                    name="membershipNumber"
                    labelKey="cat:contracts.columns.membership_number"
                  />
                  <SelectRHF
                    control={beneficiaryForm.control}
                    name="relation"
                    labelKey="cat:contracts.columns.relation"
                    options={RELATIONS.map((value) => ({ value, labelKey: `cat:contracts.relation.${value}` }))}
                  />
                  <JalaliDatePickerRHF
                    control={beneficiaryForm.control}
                    name="validFrom"
                    labelKey="cat:contracts.valid_from"
                  />
                  <JalaliDatePickerRHF
                    control={beneficiaryForm.control}
                    name="validTo"
                    labelKey="cat:contracts.valid_to"
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={!enroll.canWrite || addBeneficiary.isPending}
                    data-testid="beneficiary-add"
                  >
                    {t('cat:contracts.enroll')}
                  </Button>
                </Stack>
              </Box>
              <DataTable
                captionKey="cat:contracts.beneficiaries_caption"
                columns={beneficiaryColumns}
                rows={beneficiaries.data?.items}
                getRowId={(row) => row.id}
                isLoading={beneficiaries.isPending}
                error={beneficiaries.error}
                onRetry={() => void beneficiaries.refetch()}
                emptyTitleKey="cat:contracts.beneficiaries_empty"
              />
              <Button onClick={() => setSelected(null)}>{t('common:actions.close')}</Button>
            </Stack>
          </Paper>
        )}
      </Stack>

      <ConfirmDialog
        open={activating !== null}
        titleKey="cat:contracts.activate_title"
        messageKey="cat:contracts.activate_message"
        confirmKey="cat:contracts.activate"
        busy={transition.isPending}
        error={transition.error}
        onConfirm={() => activating && transition.mutate({ id: activating.id, action: 'activate' })}
        onCancel={() => setActivating(null)}
      />
      <ConfirmDialog
        open={suspending !== null}
        titleKey="cat:contracts.suspend_title"
        messageKey="cat:contracts.suspend_message"
        confirmKey="cat:contracts.suspend"
        busy={transition.isPending}
        error={transition.error}
        onConfirm={() => suspending && transition.mutate({ id: suspending.id, action: 'suspend' })}
        onCancel={() => setSuspending(null)}
      />
      <ConfirmDialog
        open={expiring !== null}
        titleKey="cat:contracts.expire_title"
        messageKey="cat:contracts.expire_message"
        confirmKey="cat:contracts.expire"
        destructive
        busy={transition.isPending}
        error={transition.error}
        onConfirm={() => expiring && transition.mutate({ id: expiring.id, action: 'expire' })}
        onCancel={() => setExpiring(null)}
      />
    </ListPage>
  );
}
